// src/components/GenderSelection.tsx

import React from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonText,
  IonNote,
  IonIcon,
} from '@ionic/react';
import { informationCircle } from 'ionicons/icons';

export type Gender = 'man' | 'woman' | 'non-binary' | 'other';

interface GenderSelectionProps {
  currentGenders: Gender[]; // Changed from single to array
  currentPreferences: Gender[];
  onGendersChange: (genders: Gender[]) => void; // Changed name
  onPreferencesChange: (preferences: Gender[]) => void;
}

export const GenderSelection: React.FC<GenderSelectionProps> = ({
  currentGenders,
  currentPreferences,
  onGendersChange,
  onPreferencesChange,
}) => {
  const genderOptions: { value: Gender; label: string }[] = [
    { value: 'man', label: 'Man' },
    { value: 'woman', label: 'Woman' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'other', label: 'Other' },
  ];

  const handleGenderToggle = (gender: Gender) => {
    if (currentGenders.includes(gender)) {
      const updated = currentGenders.filter(g => g !== gender);
      onGendersChange(updated);
      // If no genders selected, clear preferences
      if (updated.length === 0) {
        onPreferencesChange([]);
      }
    } else {
      onGendersChange([...currentGenders, gender]);
    }
  };

  const handlePreferenceToggle = (gender: Gender) => {
    if (currentPreferences.includes(gender)) {
      onPreferencesChange(currentPreferences.filter(g => g !== gender));
    } else {
      onPreferencesChange([...currentPreferences, gender]);
    }
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Gender & Gaming Comfort</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {/* Optional notice */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          padding: '12px',
          background: 'var(--ion-color-light)',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <IonIcon icon={informationCircle} style={{ fontSize: '24px', color: 'var(--ion-color-primary)', flexShrink: 0, marginTop: '2px' }} />
          <IonText color="medium">
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.4' }}>
              <strong>Optional comfort feature:</strong> Board gaming can sometimes feel like a male-dominated space.
              This optional tool helps everyone find gaming groups where they feel comfortable and welcome.
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', lineHeight: '1.4' }}>
              <strong>Important:</strong> Setting your gender will reduce the number of matches you see significantly,
              as you'll only connect with others who have also set their gender. You can decide for yourself if that trade-off
              is worth the added comfort and safety.
            </p>
          </IonText>
        </div>

        {/* Gender selection - now checkboxes */}
        <div style={{ marginBottom: '24px' }}>
          <IonLabel>
            <strong>My Gender(s)</strong>
            <IonNote> (Optional - select all that apply)</IonNote>
          </IonLabel>
          <IonNote style={{ display: 'block', marginTop: '4px', marginBottom: '8px', fontSize: '12px' }}>
            Leave all unchecked to see everyone and be visible to everyone.
          </IonNote>
          {genderOptions.map(option => (
            <IonItem
              key={option.value}
              lines="none"
              button
              onClick={() => handleGenderToggle(option.value)}
              detail={false}
            >
              <IonCheckbox
                slot="start"
                checked={currentGenders.includes(option.value)}
                onIonChange={(e) => {
                  e.stopPropagation();
                  handleGenderToggle(option.value);
                }}
              />
              <IonLabel>{option.label}</IonLabel>
            </IonItem>
          ))}
        </div>

        {/* Gender preferences (only show if user has set at least one gender) */}
        {currentGenders.length > 0 && (
          <>
            <div style={{ marginTop: '24px', marginBottom: '12px' }}>
              <IonLabel>
                <strong>I'm most comfortable gaming with:</strong>
              </IonLabel>
              <IonNote style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}>
                Select all that apply. Leave all unchecked to connect with everyone.
              </IonNote>
            </div>

            {genderOptions.map(option => (
              <IonItem
                key={option.value}
                lines="none"
                button
                onClick={() => handlePreferenceToggle(option.value)}
                detail={false}
              >
                <IonCheckbox
                  slot="start"
                  checked={currentPreferences.includes(option.value)}
                  onIonChange={(e) => {
                    e.stopPropagation();
                    handlePreferenceToggle(option.value);
                  }}
                />
                <IonLabel>{option.label}</IonLabel>
              </IonItem>
            ))}

            <IonNote style={{ display: 'block', marginTop: '12px', fontSize: '12px', padding: '0 16px' }}>
              This works both ways—you'll only connect with people whose comfort preferences align with yours.
              Your choices help everyone find gaming spaces where they feel welcome.
            </IonNote>
          </>
        )}
      </IonCardContent>
    </IonCard>
  );
};